import React, { FC, useCallback, useEffect, useState as useReactState } from 'react';
import useStateRef from 'react-usestateref';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';

import { Page, PhoneModel, PromoDeviceProps, PurchasedPhone, StepType } from '../../../state/types';
import { useState } from '../../../state/store';
import { getEligiblePhoneModels } from '../../../state/network';
import PurchasedDeviceSelector from '../../PurchasedDeviceSelector/PurchasedDeviceSelector';
import { commonSelectStyles } from '../../../state/constants';

import styles from './QualifyingDevices.module.css';

const QualifyingDevices: FC = () => {
  const { brands, page, eligibleDevices, dispatch } = useState('brands', 'page', 'eligibleDevices');
  const history = useHistory();

  const [models, setModels] = useReactState<PhoneModel[]>([]);
  const [brand, setBrand] = useReactState<string | null>(null);
  const [model, setModel] = useReactState<string | null>(null);
  const [mobilePrice, setMobilePrice] = useReactState(0);
  const [, setSamsungReward, samsungRewardRef] = useStateRef(0);
  const [, setFullSamsungReward, fullSamsungRewardRef] = useStateRef(0);
  const [, setPartialSamsungReward, partialSamsungRewardRef] = useStateRef(0);
  const [purchasedPhone, setPurchasedPhone] = useReactState<PurchasedPhone | null>(null);

  useEffect(() => {
    setSamsungReward(0);
    setPurchasedPhone(null);
    setModels([]);
    setBrand(null);
    setModel(null);
    setMobilePrice(0);
  }, [page, setSamsungReward, setPurchasedPhone, setModels, setBrand, setModel, setMobilePrice]);

  useEffect(() => {
    if (brand && brand !== 'Other') {
      getEligiblePhoneModels(brand)
        .then((phones) => {
          setModels(
            phones.map((phone) => ({
              value: `${phone.id}`,
              label: phone.model,
              priceMin: phone.bad_price,
              priceMax: phone.good_price,
              pricePromo: phone.promo_price,
            }))
          );
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [brand, setModels]);

  const handleBrandChange = useCallback(
    (option) => {
      if (option?.value === 'other') {
        setBrand('Other');
      } else {
        setBrand(option?.label || '');
      }
      setMobilePrice(0);
      setModels([]);
      setModel(null);
    },
    [setBrand, setModels, setModel, setMobilePrice]
  );

  const handleModelChange = useCallback(
    (option) => {
      setModel(option?.value || null);
      setMobilePrice(option ? +option.priceMax : 0);
      setSamsungReward(partialSamsungRewardRef.current);
      eligibleDevices.forEach((device) => {
        if (device.id === +option.value) {
          setSamsungReward(fullSamsungRewardRef.current);
        }
      });
    },
    [setModel, setMobilePrice, setSamsungReward, eligibleDevices, fullSamsungRewardRef, partialSamsungRewardRef]
  );

  const handlePhoneSelect = useCallback(
    (promoPhone: PromoDeviceProps) => () => {
      setPurchasedPhone(promoPhone.alias);
      setPartialSamsungReward(parseInt(promoPhone.promo_price));
      setFullSamsungReward(parseInt(promoPhone.promo_price) + parseInt(promoPhone.promo_price_enhancement));
      dispatch('update', {
        purchasedPhone: promoPhone.alias,
      });
      setMobilePrice(0);
      setModels([]);
      setBrand(null);
      setModel(null);
    },
    [
      setPurchasedPhone,
      dispatch,
      setModels,
      setBrand,
      setModel,
      setMobilePrice,
      setPartialSamsungReward,
      setFullSamsungReward,
    ]
  );

  const handleClick = useCallback(() => {
    dispatch('update', {
      step: StepType.NewDeviceCheck,
    });
    history.push({
      hash: `#${Page.Steps}`,
    });
  }, [dispatch, history]);

  if (page !== Page.QualifyingDevices) {
    return null;
  }

  const brandOption = brand ? brands.find((s) => s.label === brand) : null;
  const modelOption = model ? models.find((p) => p.value === model) : null;
  const selectedModelOptions = brandOption ? models : [];

  return (
    <div
      className={`${styles.QualifyingDevices} container flex flex-col text-center`}
      data-testid="QualifyingDevicesPage"
    >
      <section>
        <div>
          <h2>QUALIFYING DEVICES</h2>
        </div>
        <PurchasedDeviceSelector purchasedPhone={purchasedPhone} handlePhoneSelect={handlePhoneSelect} />
        {purchasedPhone == null && (
          <>
            <p className="lead">
              To see how you qualify for the amazing Samsung trade-in reward of <strong>Any Smartphone</strong> in{' '}
              <strong>Any Condition</strong>.
              <br />
              Please click on the phone model above that you purchased.
            </p>
            <br />
            <br />
            <br />
            <br />
          </>
        )}
        {purchasedPhone != null && (
          <>
            <div className="lead pt-6">
              <div className="mb-6">
                <label className="block text-gray-500 font-bold md:text-center mb-1 md:mb-0 pr-4" htmlFor="brand">
                  Choose Phone Vendor:
                </label>
              </div>
              <div className="text-center">
                <Select
                  inputId="brand"
                  value={brandOption}
                  onChange={handleBrandChange}
                  options={brands}
                  className={styles.SelectBlock}
                  styles={commonSelectStyles}
                />
              </div>
            </div>
            {brand !== 'Other' && (
              <div className="lead pt-6">
                <div className="mb-6">
                  <label className="block text-gray-500 font-bold md:text-center mb-1 md:mb-0 pr-4" htmlFor="model">
                    Choose Phone Model:
                  </label>
                </div>
                <div>
                  <Select
                    inputId="model"
                    isDisabled={!brand}
                    value={modelOption || null}
                    onChange={handleModelChange}
                    options={brand ? selectedModelOptions : []}
                    className={styles.SelectBlock}
                    styles={commonSelectStyles}
                  />
                </div>
              </div>
            )}
            {!modelOption && (
              <>
                <br />
                <br />
                <br />
                <br />
              </>
            )}
            {modelOption && (
              <>
                {brand === 'Samsung' && (
                  <p className="lead text-blue-600 mt-5">
                    Double your enhanced offer when you trade in any foldable smart phone.
                  </p>
                )}
                <div className="pt-6">
                  <div className="row justify-content-sm-center justify-content-md-center">
                    <div className="col-12 col-sm-8 col-md-6 col-lg-4">
                      <div className="card lead">
                        <div className="card-header">Estimated Trade-in Value *</div>
                        <div className="card-body">&euro; {mobilePrice.toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-8 col-md-6 col-lg-4">
                      <div className="card lead">
                        <div className="card-header">Samsung enhanced trade-in offer</div>
                        <div className="card-body">&euro; {samsungRewardRef.current.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="row justify-content-center">
                    <div className="col-12 col-sm-8 col-md-6 col-lg-4">
                      <div className="card lead border-dark">
                        <div className="card-header">Total Trade-in Value</div>
                        <div className="card-body text-blue-500 h3">
                          &euro; {(samsungRewardRef.current + mobilePrice).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/*}
                  <p className="lead fst-italic">
                    * Trade-in Offer available from 17<sup>th</sup> February 2023
                  </p>
                  {*/}
                  <p className="fst-italic">* Estimated trade-in value based on a device in good working condition.</p>
                  <br />
                  <br />
                  <br />
                  <br />
                </div>

                <div className="lead pt-10">
                  <p className="lead">To start the trade-in process</p>
                  <p className="lead">
                    <button
                      className="btn btn-secondary bg-dark hover:bg-dark-700 btn-lg"
                      onClick={handleClick}
                      disabled={!purchasedPhone}
                    >
                      CLICK HERE
                    </button>
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default QualifyingDevices;
